import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { capPdfRenderScale } from '../../utils/pdfRenderScale';
import {
  VIEWER_FIT_PADDING_PX,
  VIEWER_ZOOM_STEP,
  clampViewerZoom,
  computeViewerFitScale,
} from '../../utils/viewerZoom';

export type PdfJsLikeDoc = {
  numPages: number;
  getPage: (n: number) => Promise<{
    getViewport: (o: { scale: number }) => { width: number; height: number };
    getTextContent: () => Promise<{ items: { str?: string; width?: number; height?: number; transform?: number[] }[] }>;
  }>;
};

export function usePdfPaneView(fileUrl: string | null, initialPageNumber = 1) {
  const canvasWrapRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(initialPageNumber);
  const [pageWidth, setPageWidth] = useState<number | null>(null);
  const [scale, setScale] = useState(1);
  const [fitMode, setFitMode] = useState(true);
  const [loading, setLoading] = useState(!!fileUrl);
  const [error, setError] = useState<string | null>(null);
  const [renderedPageNumber, setRenderedPageNumber] = useState<number | null>(null);
  const [baseScale, setBaseScale] = useState(1);
  const [pageNaturalSize, setPageNaturalSize] = useState({ w: 612, h: 792 });
  const baseScaleRef = useRef(1);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const initialScaleSetRef = useRef(false);
  const pdfDocRef = useRef<PdfJsLikeDoc | null>(null);
  const pendingZoomAnchor = useRef<null | { nx: number; ny: number; cursorX: number; cursorY: number }>(null);

  const initialPageRef = useRef(initialPageNumber);
  initialPageRef.current = initialPageNumber;

  useEffect(() => {
    setRenderedPageNumber(null);
    setBaseScale(1);
    baseScaleRef.current = 1;
    initialScaleSetRef.current = false;
    setLoading(!!fileUrl);
    setError(null);
    setNumPages(0);
    setPageNumber(initialPageRef.current);
    pdfDocRef.current = null;
  }, [fileUrl]);

  const onDocumentLoadSuccess = useCallback(
    async (doc: PdfJsLikeDoc) => {
      pdfDocRef.current = doc;
      setNumPages(doc.numPages);
      setPageNumber(p => Math.min(Math.max(1, p), doc.numPages));
      setLoading(false);
      setError(null);
      try {
        const page = await doc.getPage(1);
        const vp = page.getViewport({ scale: 1 });
        setPageWidth(vp.width);
        setPageNaturalSize({ w: vp.width, h: vp.height });
      } catch {
        setPageWidth(612);
        setPageNaturalSize({ w: 612, h: 792 });
      }
    },
    [],
  );

  const onDocumentLoadError = useCallback((err: Error) => {
    setError(err.message || 'Failed to load PDF');
    setLoading(false);
  }, []);

  const computeFitScale = useCallback((): number | null => {
    const wrap = canvasWrapRef.current;
    if (!wrap || !pageWidth) return null;
    const pageH = pageNaturalSize.h || pageWidth * 1.294;
    const availW = wrap.clientWidth - VIEWER_FIT_PADDING_PX;
    const availH = wrap.clientHeight - VIEWER_FIT_PADDING_PX;
    return computeViewerFitScale(availW, availH, pageWidth, pageH);
  }, [pageWidth, pageNaturalSize.h]);

  useEffect(() => {
    if (pageWidth && canvasWrapRef.current && fitMode) {
      const fit = computeFitScale();
      if (fit != null) setScale(fit);
    }
  }, [pageWidth, pageNaturalSize.h, fitMode, computeFitScale]);

  const displayScale = fitMode ? (computeFitScale() ?? scale) : scale;
  const targetRenderScale = useMemo(
    () => capPdfRenderScale(displayScale, pageNaturalSize.w, pageNaturalSize.h),
    [displayScale, pageNaturalSize.w, pageNaturalSize.h],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (Math.abs(targetRenderScale - baseScaleRef.current) < 0.001) return;
    if (!initialScaleSetRef.current) {
      initialScaleSetRef.current = true;
      setBaseScale(targetRenderScale);
      baseScaleRef.current = targetRenderScale;
      return;
    }
    debounceRef.current = setTimeout(() => {
      setBaseScale(targetRenderScale);
      baseScaleRef.current = targetRenderScale;
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [targetRenderScale]);

  const zoomRatio = displayScale / Math.max(baseScale, 0.01);
  const visualWidth = pageNaturalSize.w * displayScale;
  const visualHeight = pageNaturalSize.h * displayScale;

  useEffect(() => {
    if (!fitMode || !pageWidth || !canvasWrapRef.current) return;
    const wrap = canvasWrapRef.current;
    const update = () => {
      const fit = computeFitScale();
      if (fit != null) setScale(fit);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [fitMode, pageWidth, pageNaturalSize.h, computeFitScale]);

  const captureZoomAnchor = useCallback((clientX?: number, clientY?: number) => {
    const wrap = canvasWrapRef.current;
    if (!wrap) return;
    const pageContainer = wrap.querySelector('.pdf-page-container') as HTMLElement | null;
    const rect = wrap.getBoundingClientRect();
    let cursorX = rect.width / 2;
    let cursorY = rect.height / 2;
    if (
      clientX !== undefined &&
      clientY !== undefined &&
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    ) {
      cursorX = clientX - rect.left;
      cursorY = clientY - rect.top;
    }
    if (!pageContainer) {
      pendingZoomAnchor.current = { nx: 0.5, ny: 0.5, cursorX, cursorY };
      return;
    }
    const scrollX = wrap.scrollLeft + cursorX;
    const scrollY = wrap.scrollTop + cursorY;
    const pcW = Math.max(1, pageContainer.offsetWidth);
    const pcH = Math.max(1, pageContainer.offsetHeight);
    pendingZoomAnchor.current = {
      nx: (scrollX - pageContainer.offsetLeft) / pcW,
      ny: (scrollY - pageContainer.offsetTop) / pcH,
      cursorX,
      cursorY,
    };
  }, []);

  useLayoutEffect(() => {
    const wrap = canvasWrapRef.current;
    const anchor = pendingZoomAnchor.current;
    if (!wrap || !anchor) return;
    pendingZoomAnchor.current = null;
    const pageContainer = wrap.querySelector('.pdf-page-container') as HTMLElement | null;
    if (!pageContainer) return;
    wrap.scrollLeft = Math.max(0, pageContainer.offsetLeft + anchor.nx * pageContainer.offsetWidth - anchor.cursorX);
    wrap.scrollTop = Math.max(0, pageContainer.offsetTop + anchor.ny * pageContainer.offsetHeight - anchor.cursorY);
  }, [scale, fitMode]);

  const zoomIn = useCallback(() => {
    captureZoomAnchor();
    setFitMode(false);
    setScale(s => clampViewerZoom(s * VIEWER_ZOOM_STEP));
  }, [captureZoomAnchor]);

  const zoomOut = useCallback(() => {
    captureZoomAnchor();
    setFitMode(false);
    setScale(s => clampViewerZoom(s / VIEWER_ZOOM_STEP));
  }, [captureZoomAnchor]);

  const zoomToFit = useCallback(() => setFitMode(true), []);

  const applyRemoteView = useCallback((next: { pageNumber: number; scale: number; fitMode: boolean }) => {
    setPageNumber(next.pageNumber);
    setFitMode(next.fitMode);
    setScale(next.scale);
  }, []);

  const scrollToNorm = useCallback((x: number, y: number) => {
    const wrap = canvasWrapRef.current;
    if (!wrap) return;
    const pageContainer = wrap.querySelector('.pdf-page-container') as HTMLElement | null;
    if (!pageContainer) return;
    const targetX = pageContainer.offsetLeft + (x / 100) * pageContainer.offsetWidth;
    const targetY = pageContainer.offsetTop + (y / 100) * pageContainer.offsetHeight;
    wrap.scrollLeft = Math.max(0, targetX - wrap.clientWidth / 2);
    wrap.scrollTop = Math.max(0, targetY - wrap.clientHeight / 2);
  }, []);

  const goPrev = useCallback(() => setPageNumber(p => Math.max(1, p - 1)), []);
  const goNext = useCallback(() => setPageNumber(p => Math.min(numPages || p, p + 1)), [numPages]);

  return {
    canvasWrapRef,
    pdfDocRef,
    numPages,
    pageNumber,
    setPageNumber,
    pageWidth,
    scale,
    setScale,
    fitMode,
    setFitMode,
    loading,
    error,
    renderedPageNumber,
    setRenderedPageNumber,
    baseScale,
    pageNaturalSize,
    displayScale,
    zoomRatio,
    visualWidth,
    visualHeight,
    onDocumentLoadSuccess,
    onDocumentLoadError,
    captureZoomAnchor,
    zoomIn,
    zoomOut,
    zoomToFit,
    applyRemoteView,
    scrollToNorm,
    goPrev,
    goNext,
  };
}
